import {
  LabeledValue,
  Loader,
  Panel,
  Typography,
  BaseImageList,
} from 'melp-design/components';
import { Link, Paper, Stack } from '@mui/material';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { useCompanyLanguages } from 'state/Administrators';
import {
  useSurvey,
  useSurveyResults,
  useSurveySubmission,
} from 'store/surveys';
import { useRouteParams } from 'utils/useIdParam';
import { formatPercents } from 'utils/format';
import {
  parsedQuestionResults,
  retriesToAttempts,
  useExportSurveyResultsJob,
} from 'containers/surveys';
import { Colors } from 'melp-design/style';

export const SurveySubmission: FC = () => {
  const { t, i18n } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const { id: surveyId, submissionId } = useRouteParams(
    ROUTES.surveys.training.submissions.details,
  );

  const { data: results, isLoading: isResultsLoading } =
    useSurveyResults(surveyId);
  const { data: survey, isLoading: isSurveyLoading } = useSurvey(surveyId);
  const { data: submission, isLoading: isSubmissionLoading } =
    useSurveySubmission(surveyId, submissionId);

  const { exportSurveyResults, isExporting } = useExportSurveyResultsJob();

  if (isResultsLoading || isSurveyLoading || isSubmissionLoading)
    return <Loader />;
  if (!results || !survey || !submission) return null;

  return (
    <Stack gap={2}>
      <Panel title={t('sidebar.administratorsInformation')}>
        <Stack gap={2} display="grid" gridTemplateColumns="1fr 1fr">
          {survey.type === 'training' && survey.scoringEnabled ? (
            <>
              <LabeledValue
                label={t('surveys.score.label')}
                value={
                  <Typography
                    color={
                      submission.score >= survey.minScore ? 'success' : 'error'
                    }
                    variant="h3"
                  >
                    {formatPercents(i18n.language, submission.score / 100)}
                  </Typography>
                }
              />
              <LabeledValue
                label={t('surveys.minScore.label')}
                value={formatPercents(i18n.language, survey.minScore / 100)}
              />
              <LabeledValue
                label={t('surveys.attempts.label')}
                value={[
                  submission.submissionAttempt,
                  retriesToAttempts(survey.retries),
                ].join(' / ')}
              />
            </>
          ) : null}
        </Stack>
      </Panel>

      <Panel title={t('surveys.questionsResults')}>
        <Stack gap={4}>
          {parsedQuestionResults({
            results,
            userLng: i18n.language,
            defaultLng: defaultLanguage,
          }).map((question) => {
            const submittedQuestion = submission.answers.find(
              (answer) => answer.questionId === question.id,
            );

            if (question.type === 'text_response') {
              return (
                <Stack key={question.id} gap="20px">
                  <Typography variant="h3">{question.title}</Typography>
                  <Paper
                    sx={{
                      backgroundColor: Colors.lightBackgroundGrey,
                      padding: '15px',
                    }}
                  >
                    <Typography variant="p1">
                      <Trans
                        i18nKey="surveys.openQuestionsExplanation"
                        components={[
                          <Link
                            component="button"
                            color="primary"
                            onClick={() => {
                              exportSurveyResults({ surveyId });
                            }}
                            disabled={isExporting}
                            key="0"
                          />,
                        ]}
                      />
                    </Typography>
                  </Paper>
                </Stack>
              );
            }

            return (
              <Stack key={question.id}>
                <Stack py={2}>
                  <Typography variant="h3">{question.title}</Typography>
                  {question.imageUrl ? (
                    <BaseImageList
                      items={[{ src: question.imageUrl }]}
                      imageFit="full"
                      cols={2}
                    />
                  ) : null}
                </Stack>

                {question.answers.map((answer, i) => {
                  const isSelected =
                    submittedQuestion?.optionAnswer.includes(answer.index) ??
                    false;

                  const selectedAnswerColor = isSelected
                    ? answer.isCorrect
                      ? 'success'
                      : 'error'
                    : undefined;

                  return (
                    <Stack
                      p={1}
                      sx={{ borderTop: `1px solid ${Colors.elementsGrey}` }}
                      key={i}
                    >
                      <Typography color={selectedAnswerColor}>{`${
                        answer.isCorrect ? '✔' : ''
                      } ${answer.label}`}</Typography>
                      {answer.imageUrl ? (
                        <BaseImageList
                          items={[{ src: answer.imageUrl }]}
                          imageFit="full"
                          cols={3}
                        />
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            );
          })}
        </Stack>
      </Panel>
    </Stack>
  );
};
